import { LitElement, html } from 'lit';

import { api } from '../api/index.js';
import { stateBadge } from '../components/plane-badge.js';
import { loadState, staleWarning } from '../components/load-state.js';
import { when } from '../components/time.js';
import { revisionLabel } from '../format.js';
import { ATTENTION_STATES, urgency } from '../i18n/labels.js';
import { poll } from '../poll.js';
import { currentViewer } from '../viewer.js';
import { pageStyles } from '../../styles/shared/page.js';
import { TableLabels } from '../table-labels.js';
import { tableStyles } from '../../styles/shared/tables.js';
import { formStyles } from '../../styles/shared/forms.js';
import { instancesPageStyles } from '../../styles/pages/instances-page.js';

/** Filters of the list, also reachable from the overview tiles as `?state=<filter>`. */
const FILTERS = [
  { code: 'all', label: 'All', matches: () => true },
  { code: 'attention', label: 'Need attention', matches: (instance) => ATTENTION_STATES.includes(instance.state) },
  { code: 'updating', label: 'Updating', matches: (instance) => instance.state === 'updating' },
  { code: 'running', label: 'Running', matches: (instance) => instance.state === 'running' },
];

/** Every instance, with the desired and the applied revision side by side. */
class InstancesPage extends LitElement {
  static properties = {
    filter: { state: true },
    query: { state: true },
  };

  static styles = [
    pageStyles,
    tableStyles,
    formStyles,
    instancesPageStyles,
  ];

  constructor() {
    super();
    new TableLabels(this);
    const requested = new URLSearchParams(window.location.search).get('state');
    this.filter = FILTERS.some((filter) => filter.code === requested) ? requested : 'all';
    this.query = '';
    this.poll = poll(this, async () => {
      const [instances, viewer] = await Promise.all([api.instances(), currentViewer()]);
      return { instances, viewer };
    });
  }

  setFilter(code) {
    this.filter = code;
    const url = code === 'all' ? '/workspaces' : `/workspaces?state=${code}`;
    window.history.replaceState({}, '', url);
  }

  visible(instances) {
    const filter = FILTERS.find((candidate) => candidate.code === this.filter);
    const query = this.query.trim().toLowerCase();
    return instances
      .filter(filter.matches)
      .filter((instance) => !query || `${instance.displayName} ${instance.name} ${instance.customer} ${instance.serverId}`.toLowerCase().includes(query))
      .sort((a, b) => urgency(a.state) - urgency(b.state) || a.name.localeCompare(b.name));
  }

  renderRevisions(instance) {
    const desired = instance.desiredRevision;
    const applied = instance.appliedRevision;
    const revision = (ref, name) => (ref ? html`<a href="/workspaces/${name}/revisions/${ref.id}">${revisionLabel(ref)}</a>` : html`<span class="muted">—</span>`);
    if (desired?.id === applied?.id) {
      return html`<td>${revision(desired, instance.ref ?? instance.name)}</td>`;
    }
    return html`<td>
      ${revision(applied, instance.ref ?? instance.name)}<span class="arrow" aria-label="to">→</span><span class="pending">${revision(desired, instance.ref ?? instance.name)}</span>
    </td>`;
  }

  renderNewInstance() {
    if (!this.poll.value?.viewer.operator) {
      return null;
    }
    return html`
      <div class="actions">
        <a class="button primary" href="/workspaces/new">New workspace</a>
      </div>
    `;
  }

  render() {
    const head = html`
      <header class="page-head">
        <div>
          <div class="page-title">
            <h1>Workspaces</h1>
            <plane-help topic="workspaces">
              <p>A workspace is an OpenClaw gateway on a server, with its assistants. Its configuration is a series of revisions: the desired one is what the workspace should run, the applied one is what its server reports running. They differ while a change is on its way, or when it failed.</p>
              <p>The state is what the server reports, never presumed.</p>
            </plane-help>
          </div>
          <p class="lead">Every workspace, what it should run and what its server reports.</p>
        </div>
        ${this.renderNewInstance()}
      </header>
    `;
    const pending = loadState(this.poll, 'the workspaces');
    if (pending) {
      return html`${head}${pending}`;
    }
    const { instances: all, viewer } = this.poll.value;
    const instances = this.visible(all);
    return html`
      ${head} ${staleWarning(this.poll)}
      <div class="toolbar">
        <div class="filters" role="group" aria-label="Filter by state">
          ${FILTERS.map(
            (filter) => html`
              <button aria-pressed=${this.filter === filter.code} @click=${() => this.setFilter(filter.code)}>
                ${filter.label} (${all.filter(filter.matches).length})
              </button>
            `,
          )}
        </div>
        <input type="text" placeholder="Search name, customer, server" aria-label="Search" .value=${this.query} @input=${(event) => (this.query = event.target.value)} />
      </div>
      ${instances.length === 0
        ? html`<p class="empty">No workspace matches.</p>`
        : html`
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Workspace</th>
                    <th>Customer</th>
                    <th>State</th>
                    <th title="Applied → desired, when they differ">Revision</th>
                    <th>Server</th>
                    <th>Image</th>
                    <th>Since</th>
                  </tr>
                </thead>
                <tbody>
                  ${instances.map(
                    (instance) => html`
                      <tr>
                        <td><a href="/workspaces/${instance.ref ?? instance.name}">${instance.displayName}</a><div class="muted"><code>${instance.name}</code></div></td>
                        <td>${instance.customer ?? html`<span class="muted">none</span>`}</td>
                        <td>${stateBadge(instance.state)}</td>
                        ${this.renderRevisions(instance)}
                        <td>
                          ${viewer.operator ? html`<a href="/servers/${instance.serverId}"><code>${instance.serverId}</code></a>` : html`<code>${instance.serverId}</code>`}
                        </td>
                        <td><code>${instance.imageVersion}</code></td>
                        <td>${when(instance.since)}</td>
                      </tr>
                    `,
                  )}
                </tbody>
              </table>
            </div>
          `}
    `;
  }
}

customElements.define('plane-instances-page', InstancesPage);
